import { useMemo } from 'react';

import { userMeDraftStore } from '../../store/users/UserMeDraftStore.ts';
import { ToastManager } from '../../ToastManager.jsx';

import { useEffectEvent } from '../../hooks/useEffectEvent';
import { useI18n } from '../../hooks/useI18nFormatters';
import { useFlowsById } from '../../store/flow/useFlows.ts';
import { useAdvancedFlowsById } from '../../store/advanced-flow/useAdvancedFlows.ts';
import { useUserMe } from '../../store/users/useUserMe.ts';
import { useApi } from '../../store/SelectedHomeyStore.js';

import { typography } from '../../components/primitives/typography';

import { Flow } from '../../components/flow/Flow';
import { AdvancedFlow } from '../../components/flow/AdvancedFlow';
import { FlowFlex } from '../../components/flow/FlowGrid';
import { ContentLoader } from '../../components/content-loader/ContentLoader';

export function FavoriteFlows({ isSiblingLoading }) {
  const { i18n } = useI18n();
  const { api } = useApi();
  const { userMe, isLoading, error } = useUserMe();
  const flows = useFlowsById();
  const advancedFlows = useAdvancedFlowsById();

  const favoriteFlowsWithType = useMemo(() => {
    if (
      flows.byId == null ||
      advancedFlows.byId == null ||
      userMe?.properties.favoriteFlows == null
    )
      return [];

    const favoriteFlowsWithType = [];

    for (const flowId of userMe.properties.favoriteFlows) {
      // Maybe favoriteFlows has been polluted.
      const id = String(flowId);

      if (flows.byId[id] != null) {
        favoriteFlowsWithType.push({
          type: 'default',
          id: id,
        });
        continue;
      }

      if (advancedFlows.byId[id] != null) {
        favoriteFlowsWithType.push({
          type: 'advanced',
          id: id,
        });
      }
    }

    return favoriteFlowsWithType;
  }, [flows.byId, advancedFlows.byId, userMe?.properties.favoriteFlows]);

  const handleRequestSwap = useEffectEvent(({ firstId, secondId }) => {
    const { favoriteFlows } = userMe.properties;

    const firstIndex = favoriteFlows.findIndex((flowId) => flowId === firstId);
    const secondIndex = favoriteFlows.findIndex((flowId) => flowId === secondId);

    const nextFavoriteFlows = [...favoriteFlows];

    const prevItem = nextFavoriteFlows[firstIndex];
    nextFavoriteFlows[firstIndex] = nextFavoriteFlows[secondIndex];
    nextFavoriteFlows[secondIndex] = prevItem;

    userMeDraftStore.setUserMeDraft({
      userMe: {
        ...userMe,
        properties: {
          ...userMe.properties,
          favoriteFlows: nextFavoriteFlows,
        },
      },
    });
  });

  const handleDragEnd = useEffectEvent(({ didDrop }) => {
    if (didDrop === true) {
      api.users
        .updateUserMeProperties({
          id: 'favoriteFlows',
          value: userMe.properties.favoriteFlows,
        })
        .then(() => {
          userMeDraftStore.setUserMeDraftDone();
        })
        .catch((error) => {
          userMeDraftStore.setUserMeDraftFailed();
          ToastManager.handleError(error);
        });
    } else {
      userMeDraftStore.setUserMeDraftFailed();
    }
  });

  if (flows.isLoading || advancedFlows.isLoading || isLoading || isSiblingLoading) {
    return (
      <FlowFlex.Root>
        {Array.from({ length: 2 }).map((_, index) => {
          return (
            <FlowFlex.Item key={index} data-animated={true}>
              <ContentLoader.Flow />
            </FlowFlex.Item>
          );
        })}
      </FlowFlex.Root>
    );
  }

  if (flows.error || advancedFlows.error || error) {
    const message = flows.error?.message ?? advancedFlows.error?.message ?? error?.message ?? '';
    return <typography.body1>{message}</typography.body1>;
  }

  let result = null;

  if (flows.byId != null && advancedFlows.byId != null) {
    result = favoriteFlowsWithType.reduce((accumulator, favoriteFlow, index) => {
      let flow = null;

      if (favoriteFlow.type === 'advanced') {
        flow = advancedFlows.byId[favoriteFlow.id];
      } else {
        flow = flows.byId[favoriteFlow.id];
      }

      if (flow == null) return accumulator;

      let component = null;

      if (favoriteFlow.type === 'advanced') {
        component = (
          <AdvancedFlow
            advancedFlowId={favoriteFlow.id}
            onRequestSwap={handleRequestSwap}
            onDragEnd={handleDragEnd}
          />
        );
      } else {
        component = (
          <Flow
            flowId={favoriteFlow.id}
            onRequestSwap={handleRequestSwap}
            onDragEnd={handleDragEnd}
          />
        );
      }

      accumulator.push(
        <FlowFlex.Item
          key={favoriteFlow.id}
          style={{
            '--animation-delay': `${10 * index}ms`,
          }}
        >
          {component}
        </FlowFlex.Item>
      );

      return accumulator;
    }, []);
  }

  if (result?.length > 0) {
    return <FlowFlex.Root>{result}</FlowFlex.Root>;
  }

  return <p>{i18n.messageFormatter(`home.noFavoriteFlows`)}</p>;
}
